import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogTitle } from '@radix-ui/react-dialog';

import { Button, DialogContent, Icon, Input } from '@/components';
import { LanguageNameMap } from '@/enums/languageCode';
import { SurveyOverviewBuilderTranslationOption } from '@/pages/Surveys/SurveysOverviewBuilder/types';

type SurveyInfoPopupProps = {
  data: SurveyOverviewBuilderTranslationOption;
  onChange: (updated: SurveyOverviewBuilderTranslationOption) => void;
  onSave: () => void;
  onCancel: () => void;
};

export const SurveyInfoPopup: FC<SurveyInfoPopupProps> = ({ data, onChange, onSave, onCancel }) => {
  const { t } = useTranslation('components');

  return (
    <DialogContent showCloseButton className="w-[460px]">
      <div className="flex flex-col gap-2">
        <DialogTitle className="text-xl font-semibold">
          {t('surveyBuilderPopup.surveyInfoPopup.title')}
        </DialogTitle>
        <div className="flex gap-2 items-center">
          <Icon name={data.languageCode} width={16} height={16} />
          <span className="text-sm font-medium">{LanguageNameMap[data.languageCode].name}</span>
        </div>
      </div>
      <div className="space-y-5 mt-6">
        <Input
          value={data.title}
          onChange={e => onChange({ ...data, title: e.target.value })}
          label="Survey title"
          className="w-full"
        />
        <Input
          as="textarea"
          value={data.description}
          onChange={e => onChange({ ...data, description: e.target.value })}
          label="Survey description"
          className="w-full"
        />
        <div className="flex flex-col gap-2">
          <p className="text-body-14-medium text text-custom-warm-grey-700">
            {t('surveyBuilderPopup.surveyInfoPopup.tags')}
          </p>
          {data.tags.map((tag, i) => (
            <Input
              key={i}
              value={tag}
              onChange={e => {
                const updatedTags = [...data.tags];
                updatedTags[i] = e.target.value;
                onChange({ ...data, tags: updatedTags });
              }}
              className="w-full"
            />
          ))}
        </div>
        <div className="flex gap-3 mt-1">
          <Button variant="secondary" type="button" size="lg" className="w-full" onClick={onCancel}>
            {t('surveyBuilderPopup.surveyInfoPopup.cancel')}
          </Button>
          <Button variant="primary" size="lg" className="w-full" onClick={onSave}>
            {t('surveyBuilderPopup.surveyInfoPopup.save')}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
};
